import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  TABLE_BODY_CELL_SX,
  TABLE_HEADER_CELL_SX,
  TABLE_PAPER_SX,
} from "../../../components/ui";
import { ClioService, ReportModel } from "../../../api";
import { MatterData } from "../../../api/models/MatterData";
import { ReportNoteRequest } from "../../../api/models/ReportNoteRequest";
import { ReportNoteResponse } from "../../../api/models/ReportNoteResponse";

type ReportLevel2 = NonNullable<ReportModel["reportLevel2"]>[number];

type RowWithSpans = ReportLevel2 & {
  _groupRowSpan?: number;
  _skipTeam?: boolean;
  _skipMatter?: boolean;
  _skipUpcoming?: boolean;
  _skipNote?: boolean;
};

const normalizeText = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  return String(v).replace(/\r\n|\n\r|\r/g, "\n");
};

const formatShortDate = (value?: string | null): string => {
  if (!value) return "";

  // expects MM/DD/YYYY
  const parts = value.split("/");
  if (parts.length !== 3) return value;

  const [mm, dd, yyyy] = parts;
  return `${mm}/${dd}/${yyyy.slice(-2)}`;
};

const formatShortDateTime = (value?: string | null): string => {
  if (!value) return "";

  const date = new Date(value);

  if (isNaN(date.getTime())) return "";

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);

  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");

  return `${mm}/${dd}/${yy} ${hh}:${min}`;
};

const computeMatterGroupSpans = (rows: ReportLevel2[]): RowWithSpans[] => {
  const out: RowWithSpans[] = [];
  let i = 0;

  while (i < rows.length) {
    const matter = rows[i]?.level2Data2 ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.level2Data2 ?? "") === matter) j++;

    const span = j - i;

    for (let k = i; k < j; k++) {
      const base: RowWithSpans = { ...rows[k] };
      if (k === i) base._groupRowSpan = span;
      else {
        base._skipTeam = true;
        base._skipMatter = true;
        base._skipUpcoming = true;
        base._skipNote = true;
      }
      out.push(base);
    }

    i = j;
  }

  return out;
};

const isOverdue = (value?: string | null): boolean => value?.toLowerCase() === "overdue";

const MATTER_GROUP_CELL_SX = {
  ...TABLE_BODY_CELL_SX,
  pb: 3.5,
  borderBottom: "7px solid",
  borderColor: "rgba(148, 163, 184, 0.55)",
};

const OVERDUE_CELL_SX = {
  bgcolor: "rgba(254, 226, 226, 0.55)",
  color: "error.dark",
  fontWeight: 600,
};

const HEAD_CELLS = [
  { label: "Team", widthPct: 7 },
  { label: "Matter", widthPct: 5 },
  { label: "Funnel", widthPct: 5 },
  { label: "Stage", widthPct: 7 },
  { label: "Task", widthPct: 15 },
  { label: "Assignee", widthPct: 6 },
  { label: "Due Date", widthPct: 5 },
  { label: "Upcoming Deadlines", widthPct: 33 },
  { label: "Note", widthPct: 17 },
] as const;

type LitigationReportTableProps = {
  data: ReportModel[];
  matterOptions: MatterData[];
	onDirtyChange?: (dirty: boolean) => void;
};

export type LitigationReportTableHandle = {
  saveAllNotes: () => Promise<void>;
  hasDirty: () => boolean;
};

export const LitigationReportTable = forwardRef<
  LitigationReportTableHandle,
  LitigationReportTableProps
>(({ data, matterOptions, onDirtyChange }, ref) => {
  const rows = useMemo(() => {
    const flat = data.flatMap((d) => d.reportLevel2 ?? []);
    return computeMatterGroupSpans(flat);
  }, [data]);

  const [notesByMatter, setNotesByMatter] = useState<Record<string, string>>({});
  const [savedNotesByMatter, setSavedNotesByMatter] = useState<Record<string, string>>({});
	const [, setSavingAll] = useState(false);
	const [expandedUpcoming, setExpandedUpcoming] = useState<Record<string, boolean>>({});
	const [upcomingOverflow, setUpcomingOverflow] = useState<Record<string, boolean>>({});
	const [noteRowByMatter, setNoteRowByMatter] = useState<Record<string, ReportNoteResponse>>({});
  const [showHistoryByMatter, setShowHistoryByMatter] = useState<Record<string, boolean>>({});

  const toTime = (v?: string | null) => {
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  };

  const isSameInstant = (a?: string | null, b?: string | null) => {
    const ta = toTime(a);
    const tb = toTime(b);
    if (ta === null || tb === null) return false;
    return ta === tb;
  };

  const buildNoteHistory = (row?: ReportNoteResponse): string[] => {
    if (!row) return [];

    const lines: string[] = [];

    // always show creator line if we have it
    if (row.creatorName && row.createdAt) {
      lines.push(`${row.creatorName} created at ${formatShortDateTime(row.createdAt)}`);
    }

    // show updater line ONLY if updatedAt != createdAt
    const showUpdate =
      row.updaterName &&
      row.updatedAt &&
      row.createdAt &&
      !isSameInstant(row.updatedAt, row.createdAt);

    if (showUpdate) {
      lines.push(`${row.updaterName} changed at ${formatShortDateTime(row.updatedAt)}`);
    }

    if (row.removerName && row.removedAt && row.note === "") {
      lines.push(`${row.removerName} removed at ${formatShortDateTime(row.removedAt)}`);
    }

    return lines;
  };

  const matterNameToId = useMemo(() => {
    const map = new Map<string, number>();
        for (const m of matterOptions) {
        // normalize to avoid whitespace/case mismatch
        const key = String(m.display_number ?? "").trim().toLowerCase();
        if (key) map.set(key, m.id);
    }
    return map;
  }, [matterOptions]);

  const matterIdToName = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of matterOptions) map.set(m.id, String(m.display_number ?? ""));
    return map;
  }, [matterOptions]);

	const getDirtyMatterKeys = (): string[] => {
		return Object.keys(notesByMatter).filter((mk) => {
			const cur = notesByMatter[mk] ?? "";
			const saved = savedNotesByMatter[mk] ?? "";
			return cur !== saved;
		});
	};

	const saveAllNotes = async (): Promise<void> => {
		const dirtyKeys = getDirtyMatterKeys();
		if (dirtyKeys.length === 0) return;

		setSavingAll(true);
		try {
			// build payloads for dirty notes only
			const payloads: ReportNoteRequest[] = dirtyKeys
				.map((matterKey) => {
					const note = notesByMatter[matterKey] ?? "";
					const matterId = matterNameToId.get(matterKey.trim().toLowerCase());
					if (!matterId) return null;

					const matterName = matterIdToName.get(matterId) ?? matterKey;
					return { matterId, matterName, note } as ReportNoteRequest;
				})
				.filter((p): p is ReportNoteRequest => p !== null);

			// save them (parallel)
			await ClioService.postApiClioSaveReportNotes(payloads);

			// mark as saved
			setSavedNotesByMatter((prev) => ({ ...prev, ...notesByMatter }));
		} catch (e) {
			console.error("Failed to save notes", e);
		} finally {
			setSavingAll(false);
		}
	};

	useImperativeHandle(ref, () => ({
		saveAllNotes,
		hasDirty: () => getDirtyMatterKeys().length > 0,
	}));

	const dirty = useMemo(
		() => Object.keys(notesByMatter).some((mk) => (notesByMatter[mk] ?? "") !== (savedNotesByMatter[mk] ?? "")),
		[notesByMatter, savedNotesByMatter]
	);

	useEffect(() => {
		onDirtyChange?.(dirty);
	}, [dirty, onDirtyChange]);


	useEffect(() => {
		if (rows.length === 0) return;
		if (matterOptions.length === 0) return;

		// matters currently visible in table
		const matterKeysInTable = Array.from(
			new Set(
				rows
					.map((r) => String(r.level2Data2 ?? "").trim())
					.filter((v) => v.length > 0)
			)
		);

		// resolve those matter names -> ids
		const matterIdsInTable = new Set<number>();
		for (const mk of matterKeysInTable) {
			const id = matterNameToId.get(mk.toLowerCase());
			if (typeof id === "number") matterIdsInTable.add(id);
		}

		if (matterIdsInTable.size === 0) return;

		let cancelled = false;

		(async () => {
			try {
				const apiRows = (await ClioService.getApiClioReportNotes()) as ReportNoteResponse[];
				console.log(apiRows)

				if (cancelled) return;

				const noteByMatterId = new Map<number, ReportNoteResponse>();

				for (const r of apiRows) {
					if (!matterIdsInTable.has(r.matterId)) continue;
					noteByMatterId.set(r.matterId, r);
				}

				const nextNotes: Record<string, string> = {};
				const nextRows: Record<string, ReportNoteResponse> = {};

				for (const mk of matterKeysInTable) {
					const id = matterNameToId.get(mk.toLowerCase());
					if (!id) continue;

					const row = noteByMatterId.get(id);
					if (!row) continue;

					nextNotes[mk] = row.note ?? "";
					nextRows[mk] = row;
				}

				setNotesByMatter(prev => ({ ...prev, ...nextNotes }));
				setSavedNotesByMatter(prev => ({ ...prev, ...nextNotes }));
				setNoteRowByMatter(prev => ({ ...prev, ...nextRows }));
			} catch (e) {
				console.error("Failed to load report notes", e);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [rows, matterOptions, matterNameToId]);

	const stopScrollBubble = (e: React.WheelEvent) => {
		const el = e.currentTarget as HTMLElement;
		const atTop = el.scrollTop === 0;
		const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

		if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
			e.preventDefault();
		}
	};


  return (
    <Paper
      variant="outlined"
      sx={{
        ...TABLE_PAPER_SX,
        maxHeight: 800,
      }}
    >
      <TableContainer
        onWheel={stopScrollBubble}
        sx={{ maxHeight: 800, overflow: "auto", overscrollBehavior: "contain" }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            {HEAD_CELLS.map((cell) => (
              <col key={cell.label} style={{ width: `${cell.widthPct}%` }} />
            ))}
          </colgroup>

          <TableHead>
            <TableRow>
              {HEAD_CELLS.map((cell) => (
                <TableCell key={cell.label} sx={TABLE_HEADER_CELL_SX}>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEAD_CELLS.length} sx={{ ...TABLE_BODY_CELL_SX, py: 8 }} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No litigation funnel rows to display.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, idx) => {
                const overdue = isOverdue(r.level2Data9);
                const matterKey = String(r.level2Data2 ?? "");
                const rawUpcoming = normalizeText(r.level2Data8);
                const isNA = rawUpcoming.trim().toLowerCase() === "n/a" || rawUpcoming.trim() === "";
                const isExpanded = expandedUpcoming[matterKey] === true;
                const showToggle = !isNA && upcomingOverflow[matterKey] === true;
                const isLastRowOfMatter =
                  idx === rows.length - 1 ||
                  String(rows[idx + 1]?.level2Data2 ?? "") !== matterKey;

                const noteRow = noteRowByMatter[matterKey];
                const historyLines = buildNoteHistory(noteRow);
                const showHistory = showHistoryByMatter[matterKey] === true;
                const taskCellSx = {
                  ...TABLE_BODY_CELL_SX,
                  whiteSpace: "pre-wrap" as const,
                  ...(overdue ? OVERDUE_CELL_SX : {}),
                  ...(isLastRowOfMatter ? MATTER_GROUP_CELL_SX : {}),
                };
                const groupCellSx = r._groupRowSpan ? MATTER_GROUP_CELL_SX : TABLE_BODY_CELL_SX;

                return (
                  <TableRow key={idx} hover>
                    {!r._skipTeam && (
                      <TableCell rowSpan={r._groupRowSpan} sx={groupCellSx}>
                        {normalizeText(r.level2Data1)}
                      </TableCell>
                    )}

                    {!r._skipMatter && (
                      <TableCell rowSpan={r._groupRowSpan} sx={{ ...groupCellSx, fontWeight: 600 }}>
                        {normalizeText(r.level2Data2)}
                      </TableCell>
                    )}

                    <TableCell sx={taskCellSx}>{normalizeText(r.level2Data3)}</TableCell>
                    <TableCell sx={taskCellSx}>{normalizeText(r.level2Data4)}</TableCell>
                    <TableCell sx={taskCellSx}>{normalizeText(r.level2Data5)}</TableCell>
                    <TableCell sx={taskCellSx}>{normalizeText(r.level2Data6)}</TableCell>
                    <TableCell sx={{ ...taskCellSx, whiteSpace: "nowrap" }}>
                      {formatShortDate(r.level2Data7)}
                    </TableCell>

                    {!r._skipUpcoming && (
                      <TableCell rowSpan={r._groupRowSpan} sx={groupCellSx}>
                        <Box
                          ref={(el: HTMLDivElement | null) => {
                            if (!el) return;
                            if (!isExpanded) {
                              const isOverflowing = el.scrollHeight > el.clientHeight + 1;
                              setUpcomingOverflow((p) =>
                                p[matterKey] === isOverflowing ? p : { ...p, [matterKey]: isOverflowing }
                              );
                            }
                          }}
                          sx={
                            isExpanded
                              ? { whiteSpace: "pre-wrap" }
                              : {
                                  display: "-webkit-box",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: 5,
                                  overflow: "hidden",
                                  whiteSpace: "pre-wrap",
                                }
                          }
                        >
                          {rawUpcoming}
                        </Box>

                        {showToggle && (
                          <Button
                            size="small"
                            onClick={() =>
                              setExpandedUpcoming((p) => ({
                                ...p,
                                [matterKey]: !isExpanded,
                              }))
                            }
                            sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: "none", fontSize: "0.75rem" }}
                          >
                            {isExpanded ? "Show less" : "Show more"}
                          </Button>
                        )}
                      </TableCell>
                    )}

                    {!r._skipNote && (
                      <TableCell
                        rowSpan={r._groupRowSpan}
                        sx={{ ...MATTER_GROUP_CELL_SX, p: 1, position: "relative", verticalAlign: "top" }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minHeight: 120 }}>
                          {historyLines.length > 0 && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              onClick={() =>
                                setShowHistoryByMatter((p) => ({ ...p, [matterKey]: !showHistory }))
                              }
                              sx={{
                                alignSelf: "flex-start",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                bgcolor: "background.paper",
                              }}
                            >
                              {showHistory ? "Hide History" : "Show History"}
                            </Button>
                          )}
                          {showHistory && historyLines.length > 0 && (
                            <Box
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 1,
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                whiteSpace: "pre-wrap",
                                bgcolor: "grey.50",
                              }}
                            >
                              {historyLines.join("\n")}
                            </Box>
                          )}
                          <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            size="small"
                            placeholder="Add note…"
                            value={notesByMatter[matterKey] ?? ""}
                            onChange={(e) =>
                              setNotesByMatter((p) => ({
                                ...p,
                                [matterKey]: e.target.value,
                              }))
                            }
                            sx={{ flex: 1, bgcolor: "background.paper" }}
                          />
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
});
